<template>
	<div class="list_item_wrap">
		<div class="bookmark_area" @click="onClickBookmark">
			<IconThinStar :icon-color="!!props.data.bookmark ? '#FFB800' : '#D9D9D9'" />
		</div>
		<div class="item_content">
			<div class="item_content_layout">
				<div v-if="indexed" class="idx">{{ props.index + 1 }}</div>
				<div class="content">
					<img
							v-if="props.data.file_path"
								:src="props.data.file_path"
								alt="logo"
								loading="lazy"
								:class="(props.data.event_name === '비공개' || props.data.event_name === '') ? 'blur-img' : ''"
							/>
							<img v-else :src="logoImg" alt="logo" loading="lazy" :class="(props.data.event_name === '비공개' || props.data.event_name === '') ? 'blur-img' : ''"
							/>
					<div class="content_txt">
						<h4 :class="(props.data.event_name === '비공개' || props.data.event_name === '') ? 'blur-text' : ''">{{ props.data.event_name || '비공개' }}</h4>
						<p>{{ props.data.event_ticker || props.data.event_code }}</p>
					</div>
				</div>
			</div>
			<button class="c_button primary-fill radius-full btn-research" @click="onClickMoveToReport">분석</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';

import { useAdjustBookmarksApi } from '@/api/Interest';
import { MonthlyCapture } from '@/api/master';
import logoImg from '@/assets/img/bi/bi.svg';
import {
	CANCEL,
	COMPANY_TITLE,
	CONFIRM_LOGIN,
	LOGIN_REQUIRED_PAGE,
	MESSAGE_CANCEL_COMPLETED,
	UNFAVORITE_PROMPT,
} from '@/constants/title';
import { useHeaderStore } from '@/stores/header';
import { useLoginPopupStore } from '@/stores/LoginPopup';
import { usePopupStore } from '@/stores/popup';
import { useToastStore } from '@/stores/toast';
import { useTokenStore } from '@/stores/token';

import IconThinStar from '../atom/icon/IconThinStar.vue';
import { useModalStore } from '@/stores/modal';

// -----variable-----
const props = defineProps<{
	data: MonthlyCapture;
	index: number;
	indexed?: boolean;
	isSector?: boolean;
	isInterest?: boolean;
}>();
const emit = defineEmits(['update:book-mark-refetch', 'update:moveToReport']);

const popupStore = usePopupStore();
const toastStore = useToastStore();
const tokenStore = useTokenStore();
const headerStore = useHeaderStore();
const loginPopupStore = useLoginPopupStore();
const router = useRouter();
const modalStore = useModalStore();

// -----api-----
const { mutateAsync: adjustBookmarks } = useAdjustBookmarksApi();

// -----function-----
const onClickMoveToReport = async () => {


	if (props.data.event_name === '비공개' || props.data.event_name === '') {
		modalStore.openModal('GuideModal');
		return;
	}

	// 한국주식 - 검색 혹은 기업분석에서의 접근은 7인 구독자에 한정, 다른 곳에서 접근은 꼭 7인 구독이 아니더라도 허용
	// 미국주식 - 7인 구독자만 접근 허용, 페이지 접근 시 router가 막아버림
	if (headerStore.getCountry === 'KR') {
		// if (!props.isInterest) {
		// 	const res = await checkAuthRefetch();
		// 	if (!res.data?.success) {
		// 		modalStore.openModal('GuideModal');
		// 		return;
		// 	}
		// }

		router.push(
			`${props.data.master_pk ? `/report/kr/${props.data.event_pk}/${props.data.master_pk}` : `/report/kr/${props.data.event_pk}`}`
		);
	} else if (headerStore.getCountry === 'JPN') {
		router.push(
			`${props.data.master_pk ? `/report/jpn/${props.data.event_pk}/${props.data.master_pk}` : `/report/jpn/${props.data.event_pk}`}`
		);
	} else if (headerStore.getCountry === 'USA') {
		router.push(
			`${props.data.master_pk ? `/report/usa/${props.data.event_pk}/${props.data.master_pk}` : `/report/usa/${props.data.event_pk}`}`
		);
	}
};

// 개별 북마크 클릭 시 실행 함수
const onClickBookmark = async () => {
	if (!tokenStore.isLogin) {
		loginPopupStore.openPopup({
			icon: 'alert',
			content: LOGIN_REQUIRED_PAGE,
			confirm: () => router.push('/auth/login'),
			confirmText: CONFIRM_LOGIN,
			cancelText: CANCEL,
		});
		return;
	}
	const params = [
		{
			event_pk: Number(props.data.event_pk),
			master_pk: Number(props.data.master_pk),
		},
	];
	if (props.data.bookmark) {
		popupStore.openPopup({
			content: COMPANY_TITLE,
			subtext: UNFAVORITE_PROMPT,
			confirm: async () => {
				const res = await adjustBookmarks({
					bookmarks: params,
				});
				if (res.data.success) {
					toastStore.openToast({
						subContent: COMPANY_TITLE,
						content: MESSAGE_CANCEL_COMPLETED,
					});
					emit('update:book-mark-refetch');
				}
			},
		});
	} else {
		const res = await adjustBookmarks({
			bookmarks: params,
		});
		if (res.data.success) {
			emit('update:book-mark-refetch');
		}
	}
};
</script>

<style scoped lang="scss">
.list_item_wrap {
	display: flex;
	min-width: 0;
	.bookmark_area {
		display: flex;
		justify-content: center;
		align-items: center;
		padding: 0.8rem;
		border-radius: 8px 0px 0px 8px;
		background: var(--color-bg-light);
		cursor: pointer;
		img {
			width: 2.4rem;
			height: 2.4rem;
		}
	}
	.item_content {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		border-radius: 0px 8px 8px 0px;
		background: var(--color-bg-mid);
		min-width: 0;
		.item_content_layout {
			display: flex;
			justify-content: start;
			align-items: center;
			gap: 0.8rem;
			padding: 1.6rem 1rem;
			min-width: 0;
		}
		.idx {
			width: 2.4rem;
			text-align: center;
			font-size: var(--font-s-small);
			font-weight: var(--font-w-bold);
			line-height: normal;
		}
		.content {
			display: flex;
			justify-content: start;
			align-items: center;
			gap: 0.8rem;
			min-width: 0;
			img {
				width: 3.2rem;
				height: 3.2rem;
				border-radius: 50%;

				&.blur-img {
					filter: blur(5px) grayscale(100%);
				}
			}
			.content_txt {
				min-width: 0;
				h4 {
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					font-size: var(--font-s-mid);
					font-weight: var(--font-w-semi-bold);
					line-height: normal;

					&.blur-text {
						filter: blur(5px);
					}
				}
				p {
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					color: #c6c6d6;
					font-size: var(--font-s-small);
					font-weight: var(--font-w-light);
					line-height: normal;
				}
			}
		}
		.btn-research {
			display: flex;
			width: 5.8rem;
			height: auto; // c_button의 height unset
			padding: 0.6rem 1.6rem;
			justify-content: center;
			align-items: center;
			gap: 10px;
			margin-right: 1rem;
			font-size: var(--font-s-mid);
			font-weight: var(--font-w-semi-bold);
			line-height: normal;
		}
	}
}
</style>
